import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { AnimatedMark } from './AnimatedMark';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isHome = location.pathname === '/';

  const navLinks = [
    { name: 'Approach', href: '#approach' },
    { name: 'Creator Backend', href: '#creator-backend' },
    { name: 'Founder', href: '#founder' },
    { name: 'Contact', href: '#contact' },
  ];

  const linkHref = (hash) => (isHome ? hash : `/${hash}`);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 px-4 md:px-6 pt-3.5 transition-all"
      data-testid="main-nav"
    >
      <div className="max-w-[1160px] mx-auto">
        <div
          className={`flex items-center justify-between gap-4 rounded-full py-2 pl-4 pr-2 md:pl-[18px] transition-all duration-300 ${
            scrolled
              ? 'bg-[rgba(12,17,31,0.72)] backdrop-blur-[14px] border border-[rgba(255,255,255,0.16)] shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)]'
              : 'bg-transparent border border-transparent'
          }`}
          style={{ WebkitBackdropFilter: scrolled ? 'blur(14px) saturate(140%)' : 'none' }}
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <AnimatedMark size={34} idPrefix="nav" />
            <div className="hidden sm:block leading-[1.1]">
              <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>
                Solidcore Venture
              </div>
              <div style={{ fontFamily: 'var(--body)', fontWeight: 500, fontSize: 11.5, color: 'var(--muted)' }}>
                Built around customers, not clicks
              </div>
            </div>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-7 text-sm" style={{ color: 'var(--muted)' }}>
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={linkHref(link.href)}
                className="hover:text-white transition-colors"
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA */}
          <div className="flex items-center gap-2">
            <a
              href={linkHref('#contact')}
              className="btn btn-gold hidden sm:inline-flex"
              style={{ padding: '12px 20px', fontSize: '14.5px', borderRadius: 999 }}
              data-testid="nav-cta-button"
            >
              Start a conversation <span className="arrow">→</span>
            </a>
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {isOpen && (
          <div
            className="lg:hidden mt-2 rounded-2xl p-4 space-y-1"
            style={{
              background: 'rgba(12,17,31,0.92)',
              backdropFilter: 'blur(14px)',
              border: '1px solid var(--line-2)',
            }}
            data-testid="mobile-menu"
          >
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={linkHref(link.href)}
                onClick={() => setIsOpen(false)}
                className="block px-4 py-3 text-sm font-medium rounded-lg transition-colors hover:bg-white/5"
                style={{ color: 'var(--muted)' }}
              >
                {link.name}
              </a>
            ))}
            <a
              href={linkHref('#contact')}
              onClick={() => setIsOpen(false)}
              className="btn btn-gold w-full mt-3"
              style={{ padding: '14px 20px', fontSize: '15px' }}
            >
              Start a conversation →
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
